import { type ReactNode, useCallback, useRef, useState } from 'react'

import { ConfirmModal } from '@/components/ui/ConfirmModal'

import {
  ConfirmContext,
  type ConfirmContextValue,
  type ConfirmOptions,
} from './confirmContext'

interface ConfirmProviderProps {
  children: ReactNode
}

/**
 * Owns the app's single confirmation dialog.
 *
 * Callers ask a question and await the answer — `if (!(await confirm(...))) return`
 * — instead of threading open/close state and a callback through the component
 * that raised it. Holding the one instance here also means a caller cannot forget
 * to render the dialog and leave itself waiting on a promise nothing settles.
 */
export const ConfirmProvider = ({ children }: ConfirmProviderProps) => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  // Held in a ref rather than state because settling is a side effect, and a
  // state updater must stay pure to survive being invoked twice.
  const resolveRef = useRef<((confirmed: boolean) => void) | null>(null)

  const settle = useCallback((confirmed: boolean) => {
    resolveRef.current?.(confirmed)
    resolveRef.current = null
    setOptions(null)
  }, [])

  // Stable identity: this function *is* the context value, so re-creating it
  // would re-render every consumer.
  const confirm = useCallback<ConfirmContextValue>(
    (next) =>
      new Promise<boolean>((resolve) => {
        // A second question asked while one is open would strand the first
        // caller forever, so the earlier one is answered "no" as it is replaced.
        resolveRef.current?.(false)
        resolveRef.current = resolve
        setOptions(next)
      }),
    []
  )

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options !== null && (
        <ConfirmModal
          title={options.title}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          destructive={options.destructive}
          onConfirm={() => settle(true)}
          onCancel={() => settle(false)}
        >
          {options.body}
        </ConfirmModal>
      )}
    </ConfirmContext.Provider>
  )
}
